<script setup>
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'

const props = defineProps({
  fetchPage: {
    type: Function,
    required: true,
  },
  formatPrice: {
    type: Function,
    required: true,
  },
  getLink: {
    type: Function,
    required: true,
  },
  reloadKey: {
    type: [String, Number],
    default: '',
  },
  target: {
    type: String,
    default: '_self',
  },
  title: {
    type: String,
    default: '',
  },
  pageSize: {
    type: Number,
    default: 12,
  },
})

const emit = defineEmits(['items-change'])

const items = ref([])
const page = ref(0)
const loading = ref(false)
const loadError = ref(false)
const hasMore = ref(true)
const autoLoad = ref(false)
const sentinelRef = ref(null)
let observer = null
let requestId = 0

function getItemId(item) {
  return String(item?.offerId || item?.itemId || item?.id || item?.item_id || item?.itemID || '').trim()
}

function getItemImage(item) {
  return String(item?.imageUrl || item?.mainImageUrl || item?.image || item?.pictUrl || item?.pic || '').trim()
}

function getItemTitle(item) {
  return String(item?.title || item?.subjectTrans || item?.subject || '').trim()
}

function getItemSold(item) {
  return item?.soldCount ?? item?.sold_count ?? item?.sold ?? item?.monthSold ?? item?.saleQuantity ?? ''
}

function publishItems() {
  emit('items-change', [...items.value])
}

async function loadPage({ reset = false } = {}) {
  if (loading.value) return

  const currentRequestId = ++requestId
  const nextPage = reset ? 1 : page.value + 1
  loading.value = true
  loadError.value = false

  try {
    const result = await props.fetchPage({
      page: nextPage,
      pageSize: props.pageSize,
    })
    if (currentRequestId !== requestId) return

    const rawItems = Array.isArray(result) ? result : result?.items
    const nextRawItems = Array.isArray(rawItems) ? rawItems : []
    const existingIds = new Set((reset ? [] : items.value).map(getItemId).filter(Boolean))
    const nextItems = nextRawItems.filter((item) => {
      const id = getItemId(item)
      if (!id || existingIds.has(id)) return false
      existingIds.add(id)
      return true
    })

    items.value = reset ? nextItems : [...items.value, ...nextItems]
    page.value = nextPage
    publishItems()

    const total = Number(result?.total)
    const reachedTotal = Number.isFinite(total) && total > 0 && items.value.length >= total
    const exhaustedPage = nextRawItems.length < props.pageSize
    const duplicatePage = !reset && nextRawItems.length > 0 && nextItems.length === 0
    hasMore.value = typeof result?.hasMore === 'boolean'
      ? result.hasMore && !duplicatePage
      : !(reachedTotal || exhaustedPage || duplicatePage)
  } catch (error) {
    if (currentRequestId !== requestId) return
    loadError.value = true
  } finally {
    if (currentRequestId === requestId) loading.value = false
  }

  if (process.client && currentRequestId === requestId && autoLoad.value && hasMore.value && !loadError.value) {
    await nextTick()
    const rect = sentinelRef.value?.getBoundingClientRect?.()
    if (rect && rect.top <= window.innerHeight + 240 && rect.bottom >= -240) loadPage()
  }
}

async function reload() {
  requestId += 1
  items.value = []
  page.value = 0
  loading.value = false
  loadError.value = false
  hasMore.value = true
  autoLoad.value = false
  publishItems()
  await loadPage({ reset: true })
}

async function startAutoLoad() {
  if (loading.value || !hasMore.value) return
  autoLoad.value = true
  await loadPage()
}

async function retry() {
  if (loading.value) return
  if (!items.value.length) {
    await reload()
    return
  }
  await loadPage()
}

function handleIntersection(entries) {
  const entry = entries[0]
  if (!entry?.isIntersecting || !autoLoad.value || loading.value || loadError.value || !hasMore.value) return
  loadPage()
}

function observeSentinel(el) {
  if (observer && el) observer.observe(el)
}

watch(
  () => props.reloadKey,
  () => reload(),
  { immediate: true, flush: 'post' },
)

watch(sentinelRef, (el, previousEl) => {
  if (observer && previousEl) observer.unobserve(previousEl)
  observeSentinel(el)
})

onMounted(() => {
  if (!('IntersectionObserver' in window)) return
  observer = new IntersectionObserver(handleIntersection, {
    rootMargin: '0px 0px 240px',
    threshold: 0.01,
  })
  observeSentinel(sentinelRef.value)
})

onBeforeUnmount(() => {
  requestId += 1
  if (observer) {
    observer.disconnect()
    observer = null
  }
})

defineExpose({
  items,
  reload,
})
</script>

<template>
  <section class="product-recommendation">
    <div class="product-recommendation__header">
      <img src="@/public/imgs/HeaderDecoration_Right.png"  class="product-recommendation__sparkle"/>
      <h2 class="product-recommendation__title">
        {{ title || $t('tbDetail.hotRecommend') }}
      </h2>
      <img src="@/public/imgs/HeaderDecoration_Right.png"  class="product-recommendation__sparkle"/>

    </div>

    <div class="product-recommendation__body">
      <div v-if="loading && !items.length" class="product-recommendation__grid" aria-hidden="true">
        <div v-for="index in pageSize" :key="index" class="product-recommendation-card product-recommendation-card--skeleton">
          <span class="product-recommendation-card__skeleton-img" />
          <span class="product-recommendation-card__skeleton-line" />
          <span class="product-recommendation-card__skeleton-line product-recommendation-card__skeleton-line--short" />
        </div>
      </div>

      <div v-else-if="items.length" class="product-recommendation__grid">
        <a
          v-for="item in items"
          :key="getItemId(item)"
          class="product-recommendation-card"
          :href="getLink(item)"
          :target="target"
        >
          <el-image :src="getItemImage(item)" fit="cover" class="product-recommendation-card__img" referrerpolicy="no-referrer" />
          <div class="product-recommendation-card__info">
            <div class="product-recommendation-card__title">
              {{ getItemTitle(item) }}
            </div>
            <div class="product-recommendation-card__price-row">
              <span class="product-recommendation-card__price">
                {{ formatPrice(item) }}
              </span>
              <span v-if="getItemSold(item)" class="product-recommendation-card__sold">
                {{ $t('tbDetail.soldCount', { count: getItemSold(item) }) }}
              </span>
            </div>
          </div>
        </a>
      </div>

      <div ref="sentinelRef" class="product-recommendation__status" aria-live="polite">
        <button
          v-if="hasMore && !autoLoad && !loading && !loadError"
          type="button"
          class="product-recommendation__more"
          @click="startAutoLoad"
        >
          {{ $t('tbDetail.viewMore') }}
        </button>
        <button
          v-else-if="loading"
          type="button"
          class="product-recommendation__more product-recommendation__more--loading"
          disabled
        >
          <span class="product-recommendation__spinner" aria-hidden="true" />
          {{ $t('tbDetail.loadingMore') }}
        </button>
        <span v-else-if="loadError" class="product-recommendation__status-text product-recommendation__status-text--error">
          <img src="@/public/imgs/error_icon.png" class="product-recommendation__status-icon" />
          {{ $t('tbDetail.recommendLoadFailed') }}

          <button type="button" class="product-recommendation__retry" @click="retry">
            {{ $t('tbDetail.retry') }}
          </button>
        </span>
        <span v-else-if="items.length && !hasMore" class="product-recommendation__status-text">
          {{ $t('tbDetail.recommendAllShown') }}
        </span>
        <span v-else-if="!items.length" class="product-recommendation__status-text">
          {{ $t('tbDetail.recommendEmpty') }}
        </span>
      </div>
    </div>
  </section>
</template>

<style scoped>
.product-recommendation {
  width: 100%;
  margin-top: 16px;
  padding: 20px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  background: #fff;
}

.product-recommendation__header {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding-bottom: 8px;
}

.product-recommendation__title {
  margin: 0;
  color: var(--text-1, #1a1817);
  font-family: 'Noto Sans TC', sans-serif;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
}

.product-recommendation__sparkle {
  width: 28.8px;
  height: 24px;
}

.product-recommendation__body {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.product-recommendation__grid {
  width: calc(100% - 40px);
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 176px));
  justify-content: space-between;
  column-gap: 8px;
  row-gap: 20px;
}

.product-recommendation-card {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  color: inherit;
  text-decoration: none;
  cursor: pointer;
  transition: transform 0.15s ease;
}

.product-recommendation-card:hover {
  transform: translateY(-2px);
}

.product-recommendation-card__img {
  width: 100%;
  aspect-ratio: 1;
  overflow: hidden;
  border-radius: 2px;
  background: #f3f4f6;
}

.product-recommendation-card__img :deep(img) {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.product-recommendation-card__info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.product-recommendation-card__title {
  overflow: hidden;
  color: #232323;
  font-family: 'Inter', 'Noto Sans JP', sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.product-recommendation-card__price-row {
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.product-recommendation-card__price {
  flex-shrink: 0;
  color: var(--price, #ff4600);
  font-family: 'Inter', sans-serif;
  font-size: 18px;
  font-weight: 600;
  line-height: 24px;
  white-space: nowrap;
}

.product-recommendation-card__sold {
  min-width: 0;
  flex-shrink: 1;
  overflow: hidden;
  color: var(--text-3, #908380);
  font-family: 'Noto Sans TC', sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.product-recommendation__status {
  width: 100%;
  height: 44px;
  padding-top: 20px;
  display: flex;
  align-items: flex-start;
  justify-content: center;
}

.product-recommendation__more,
.product-recommendation__retry {
  min-width: 200px;
  height: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: 1px solid #FF7228;
  border-radius: 2px;
  background: #fff;
  color: var(--brand, #ff7228);
  font: 500 20px/18px 'Noto Sans TC', sans-serif;
  cursor: pointer;
}

.product-recommendation__more--loading {
  cursor: wait;
}


.product-recommendation__more:hover,
.product-recommendation__retry:hover {
  border-color: var(--brand, #ff7228);
  background: var(--orange-50, #fff5ed);
}

.product-recommendation__status-text {
  min-height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: var(--text-3, #908380);
  font-size: 12px;
  line-height: 18px;
}

.product-recommendation__status-text--error {
  color: #d93025;
  .product-recommendation__status-icon{
    width: 16px;
    height: 16px;
  }
  .product-recommendation__retry {
    margin-left: 12px;
  }
}

.product-recommendation__retry {
  padding: 2px 10px;
}

.product-recommendation__spinner {
  width: 16px;
  height: 16px;
  border: 2px solid var(--orange-100, #ffe8d4);
  border-top-color: var(--brand, #ff7228);
  border-radius: 50%;
  animation: product-recommendation-spin 0.8s linear infinite;
}

.product-recommendation-card--skeleton {
  cursor: default;
}

.product-recommendation-card--skeleton:hover {
  transform: none;
}

.product-recommendation-card__skeleton-img,
.product-recommendation-card__skeleton-line {
  display: block;
  border-radius: 2px;
  background: linear-gradient(90deg, #f3f1f0 25%, #faf9f8 37%, #f3f1f0 63%);
  background-size: 400% 100%;
  animation: product-recommendation-skeleton 1.4s ease infinite;
}

.product-recommendation-card__skeleton-img {
  width: 100%;
  aspect-ratio: 1;
}

.product-recommendation-card__skeleton-line {
  width: 100%;
  height: 14px;
}

.product-recommendation-card__skeleton-line--short {
  width: 58%;
  height: 18px;
}

@keyframes product-recommendation-spin {
  to { transform: rotate(360deg); }
}

@keyframes product-recommendation-skeleton {
  0% { background-position: 100% 50%; }
  100% { background-position: 0 50%; }
}

@media (max-width: 980px) {
  .product-recommendation__grid {
    width: calc(100% - 24px);
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 12px;
  }
}

@media (max-width: 640px) {
  .product-recommendation__grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>
